import React, { useState, useRef, useCallback, useMemo } from "react";
import { ConfigProvider } from "antd";
import "react-quill/dist/quill.snow.css";
import Modal from "./components/Modal";
import articleService from "../../../service/article.service";
import StatusHandler from "../../../components/articles/StatusHandler";
import { useArticleManagement } from "../../../hooks/useArticleManagement";
import { useArticleNavigation } from "../../../hooks/useArticleNavigation";
import ArticleSearch from "../../../components/articles/ArticleSearch";
import ArticleTable from "../../../components/articles/ArticleTable";
import Pagination from "../../../components/articles/Pagination";
import {
  MdFileUpload,
  MdImage,
  MdCheckCircle,
  MdPublishedWithChanges,
  MdAssignment,
} from "react-icons/md";
import Swal from "sweetalert2";
import DocumentContentModal from "../../../components/articles/ArticleDocumentModal";
import { antTheme, colors, tailwindClasses } from "../../../utils/theme";
import useUserRole from "./../../../utils/useUserRole";

const ArticlePubliable = () => {
  const fileInputRef = useRef(null);
  const [coverImage, setCoverImage] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(null);
  const [isLoading] = useState(false);
  const userRole = useUserRole();

  // Hook de navigation réutilisable
  const { navigateToDetails, navigateToEdit } = useArticleNavigation();

  // Utilisation du hook partagé avec navigation personnalisée
  const {
    error,
    searchTerm,
    currentItems,
    handleSearch,
    getArticleTitle,
    getArticleAuthor,
    getArticleDate,
    fetchArticles,
    currentPage,
    handlePageChange,
    itemsPerPage,
    handleItemsPerPageChange,
    indexOfFirstItem,
    indexOfLastItem,
    totalItems,
    totalPages,
    isModalOpen,
    setIsModalOpen,
    modalTitle,
    setModalTitle,
    modalSize,
    setModalSize,
    selectedArticle,
    setSelectedArticle,
    isSubmitting,
    setIsSubmitting,
    submitError,
    setSubmitError,
    submitSuccess,
    setSubmitSuccess,
    modalContent,
    setModalContent,
  } = useArticleManagement(() => articleService.getAllByStatus(3), null, {
    enableModal: true,
    enableEdit: false,
    customNavigationHandler: navigateToDetails,
  });

  // Callback de mise à jour de statut
  const handleStatusUpdate = useCallback(async ({ article, newStatus, comment }) => {
    console.log("🔄 Callback mise à jour statut article publiable:", {
      articleId: article.id_art,
      fromStatus: 3, // Publiable
      toStatus: newStatus,
      commentLength: comment.length,
      user: "HiQaody",
      timestamp: "2025-08-31 07:45:45",
    });

    try {
      await fetchArticles();
      setIsModalOpen(false);
      
      console.log("✅ Articles publiables rechargés après changement statut:", {
        newTotalItems: totalItems,
        user: "HiQaody",
        timestamp: "2025-08-31 07:45:45",
      });
    } catch (error) {
      console.error("❌ Erreur lors du rechargement des articles publiables:", {
        error: error.message,
        user: "HiQaody",
        timestamp: "2025-08-31 07:45:45",
      });
    }
  }, [fetchArticles, setIsModalOpen, totalItems]);

  // Configuration personnalisée pour ArticlePubliable
  const customStatusFilter = useCallback((status) => {
    // Inclure uniquement les statuts pertinents pour les articles publiables
    // 2: À remanier, 3: Publiable, 5: Publié, 7: Refusé
    return [2, 3, 5, 7].includes(status.id_stat);
  }, []);

  // Règles d'avertissement spécifiques pour ArticlePubliable
  const warningRules = {
    downgradeFromPublishable: {
      condition: (orig, new_stat) => 
        (orig === '3' || orig === 3) && (new_stat === '2' || new_stat === 2),
      title: '⚠️ Attention: Rétrogradation',
      description: 'Vous allez rétrograder un article "publiable" vers "à remanier". Cette action nécessite une justification détaillée.',
      config: {
        title: '⚠️ Rétrogradation d\'article publiable',
        text: 'Vous êtes sur le point de rétrograder un article publiable vers "à remanier". Cette action est inhabituelle et nécessite une justification détaillée.',
        icon: 'warning',
        confirmButtonText: 'Oui, je comprends les implications'
      }
    },
    toPublished: {
      condition: (orig, new_stat) => new_stat === '5' || new_stat === 5,
      config: {
        title: '🚀 Publication d\'article',
        text: 'Vous êtes sur le point de publier définitivement cet article. Il sera visible publiquement.',
        icon: 'success',
        confirmButtonText: 'Confirmer la publication'
      }
    },
    toRejected: {
      condition: (orig, new_stat) => 
        (orig === '3' || orig === 3) && (new_stat === '7' || new_stat === 7),
      config: {
        title: '❌ Refus d\'article publiable',
        text: 'Vous refusez un article qui était marqué comme publiable. Cette action nécessite une justification solide.',
        icon: 'error',
        confirmButtonText: 'Confirmer le refus'
      }
    },
    maintainPublishable: {
      condition: (orig, new_stat) => 
        (orig === '3' || orig === 3) && (new_stat === '3' || new_stat === 3),
      config: {
        title: '✅ Confirmation du statut publiable',
        text: 'Vous confirmez que cet article reste publiable. Un nouveau commentaire sera ajouté.',
        icon: 'info'
      }
    }
  };

  // Suggestions de commentaires pour ArticlePubliable
  const suggestionComments = [
    "Article excellent, prêt pour publication immédiate",
    "Qualité exceptionnelle, recommandé pour publication prioritaire",
    "Contribution significative au domaine, validation finale approuvée",
    "Méthodologie solide et résultats convaincants - Publiable",
    "Article bien rédigé, respecte tous les standards de publication",
    "Validation éditoriale terminée, article prêt pour diffusion",
    "Révisions mineures nécessaires avant publication finale",
    "Clarifications requises sur certains points techniques",
    "Mise à jour des références bibliographiques recommandée",
    "Ajustements stylistiques suggérés pour améliorer la lisibilité"
  ];

  // Définir handleDocumentAdded AVANT handleContentArticle
  const handleDocumentAdded = useCallback((newDocument) => {
    console.log("📄 Document ajouté à l'article publiable:", {
      documentId: newDocument?.id,
      user: "HiQaody",
      timestamp: "2025-08-31 07:45:45",
    });

    Swal.fire({
      icon: "success",
      title: "Document ajouté",
      text: "Le document a été ajouté avec succès à l'article",
      confirmButtonColor: colors.primary,
      timer: 3000,
      timerProgressBar: true,
    });
    window.location.reload();
  }, []);

  // Gestion du contenu de l'article
  const handleContentArticle = useCallback(
    (article) => {
      console.log("📖 Ouverture contenu article publiable:", {
        articleId: article.id_art,
        title: getArticleTitle(article),
      });

      setSelectedArticle(article);
      setModalTitle("Contenu de l'article");
      setModalSize("4xl");
      setModalContent(
        <DocumentContentModal
          isOpen={true}
          onClose={() => setIsModalOpen(false)}
          articleId={article}
          onSuccess={handleDocumentAdded}
        />
      );
      setIsModalOpen(true);
    },
    [
      setIsModalOpen,
      setModalContent,
      setModalSize,
      setModalTitle,
      setSelectedArticle,
      handleDocumentAdded,
      getArticleTitle,
    ]
  );

  // Gestion de la publication d'article
  const handlePublishArticle = useCallback(
    (article) => {
      console.log("🚀 Initialisation publication article:", {
        articleId: article.id_art,
        title: getArticleTitle(article),
        user: "HiQaody",
        timestamp: "2025-08-31 07:45:45",
      });

      setSelectedArticle(article);
      setSubmitError(null);
      setSubmitSuccess(false);
      setModalTitle("Publier cet article");
      setModalSize("md");
      setIsModalOpen(true);
      setCoverImage(null);
      setCoverImagePreview(null);
    },
    [
      setIsModalOpen,
      setModalSize,
      setModalTitle,
      setSelectedArticle,
      setSubmitError,
      setSubmitSuccess,
      getArticleTitle,
    ]
  );

  // Gestion du changement d'image de couverture
  const handleCoverImageChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      console.log("🖼️ Sélection image de couverture:", {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        user: "HiQaody",
        timestamp: "2025-08-31 07:45:45",
      });

      setCoverImage(file);
      const objectUrl = URL.createObjectURL(file);
      setCoverImagePreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
  }, []);

  const triggerFileInput = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  // Publication de l'article
  const handleSubmitPublication = useCallback(async () => {
    if (!coverImage) {
      setSubmitError("Veuillez sélectionner une image de couverture.");
      return;
    }

    console.log("🚀 Soumission publication article:", {
      articleId: selectedArticle.id_art,
      coverImageName: coverImage.name,
      coverImageSize: coverImage.size,
      user: "HiQaody",
      timestamp: "2025-08-31 07:45:45",
    });

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const formData = new FormData();
      formData.append("photo_art", coverImage);

      await articleService.publishArticle(selectedArticle.id_art, formData);

      setSubmitSuccess(true);
      setIsModalOpen(false);

      console.log("✅ Article publié avec succès:", {
        articleId: selectedArticle.id_art,
        user: "HiQaody",
        timestamp: "2025-08-31 07:45:45",
      });

      await Swal.fire({
        icon: "success",
        title: "Publié !",
        text: "L'article a été publié avec succès",
        confirmButtonColor: colors.primary,
        timer: 3000,
        timerProgressBar: true,
      });

      await fetchArticles();
    } catch (error) {
      const errorMsg =
        error.response?.data?.message ||
        "Une erreur s'est produite lors de la publication.";
      
      console.error("❌ Erreur lors de la publication:", {
        error: errorMsg,
        articleId: selectedArticle.id_art,
        user: "HiQaody",
        timestamp: "2025-08-31 07:45:45",
      });

      setSubmitError(errorMsg);

      await Swal.fire({
        icon: "error",
        title: "Erreur de publication",
        text: errorMsg,
        confirmButtonColor: colors.danger,
        confirmButtonText: "OK",
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    coverImage,
    fetchArticles,
    selectedArticle,
    setIsModalOpen,
    setIsSubmitting,
    setSubmitError,
    setSubmitSuccess,
  ]);

  // Mémoriser le contenu du modal de publication
  const publicationModalContent = useMemo(() => {
    if (!selectedArticle || modalTitle !== "Publier cet article") return null;

    return (
      <div className="p-6">
        <div className="mb-4">
          <h3 className="mb-2 text-lg font-medium text-gray-900">
            {getArticleTitle(selectedArticle)}
          </h3>
          <p className="text-sm text-gray-600">
            Vous êtes sur le point de publier cet article. Veuillez ajouter une
            image de couverture.
          </p>
        </div>
        <div className="mt-6 space-y-4">
          <div>
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Photo de couverture
              </label>
              <span className="text-xs text-gray-500">
                Formats acceptés: JPG, JPEG, PNG
              </span>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              onChange={handleCoverImageChange}
              accept="image/jpeg,image/png,image/jpg"
            />
            {coverImagePreview ? (
              <div className="mt-2">
                <div className="relative overflow-hidden rounded-lg">
                  <img
                    src={coverImagePreview}
                    alt="Aperçu de l'image de couverture"
                    className="h-64 w-full object-cover"
                  />
                  <button
                    onClick={triggerFileInput}
                    className="absolute bottom-2 right-2 rounded-full bg-gray-800 bg-opacity-70 p-2 text-white hover:bg-opacity-90"
                    title="Changer l'image"
                    type="button"
                  >
                    <MdFileUpload className="h-5 w-5" />
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {coverImage?.name} (
                  {(coverImage.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              </div>
            ) : (
              <div
                onClick={triggerFileInput}
                className="mt-2 flex h-64 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100"
              >
                <div className="flex flex-col items-center justify-center pb-6 pt-5">
                  <MdImage className="mb-3 h-12 w-12 text-gray-400" />
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">
                      Cliquez pour téléverser
                    </span>{" "}
                    ou glissez-déposez
                  </p>
                  <p className="text-xs text-gray-500">
                    Image de couverture pour l'article
                  </p>
                </div>
              </div>
            )}
          </div>
          {submitError && (
            <div
              className={
                tailwindClasses.alertError ||
                "mt-4 rounded-md bg-red-50 p-4 text-sm text-red-800"
              }
            >
              <p>{submitError}</p>
            </div>
          )}
          {submitSuccess && (
            <div
              className={
                tailwindClasses.alertSuccess ||
                "mt-4 rounded-md bg-green-50 p-4 text-sm text-green-800"
              }
            >
              <div className="flex items-center">
                <MdCheckCircle className="mr-2 h-5 w-5" />
                <p>Article publié avec succès!</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }, [
    selectedArticle,
    modalTitle,
    getArticleTitle,
    coverImage,
    coverImagePreview,
    handleCoverImageChange,
    triggerFileInput,
    submitError,
    submitSuccess,
  ]);

  // États de chargement et d'erreur
  if (isLoading) {
    console.log("⏳ Chargement des articles publiables:", {
      user: "HiQaody",
      timestamp: "2025-08-31 07:45:45",
    });

    return (
      <div className="flex h-96 items-center justify-center">
        <div
          className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2"
          style={{ borderColor: colors.primary }}
        ></div>
      </div>
    );
  }

  if (error) {
    console.error("❌ Erreur lors du chargement des articles publiables:", {
      error,
      user: "HiQaody",
      timestamp: "2025-08-31 07:45:45",
    });

    return (
      <div
        className={
          tailwindClasses.alertError ||
          "rounded-lg bg-red-50 p-4 text-center text-red-800"
        }
      >
        <p>{error}</p>
        <button
          onClick={fetchArticles}
          className="mt-4 rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-700"
        >
          Réessayer
        </button>
      </div>
    );
  }

  console.log("🖼️ Rendu ArticlePubliable:", {
    totalItems,
    currentPage,
    userRole,
    hasItems: currentItems.length > 0,
    user: "HiQaody",
    timestamp: "2025-08-31 07:45:45",
  });

  return (
    <ConfigProvider theme={antTheme}>
      <div className="container mx-auto px-4 py-6">
        <ArticleSearch
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          title="Articles publiables"
          subtitle="Liste des articles publiables en attente d'attribution des statuts"
          primaryColor={colors.primary}
        />

        <StatusHandler
          onStatusUpdate={handleStatusUpdate}
          showConfirmation={true}
          customStatusFilter={customStatusFilter}
          warningRules={warningRules}
          minimumCommentLength={15} // Standard pour articles publiables
          suggestionComments={suggestionComments}
        >
          {({ 
            initializeStatusAttribution, 
            selectedArticle: statusSelectedArticle,
            StatusSelector,
            CommentInput, 
            StatusMessages,
            handleSubmitStatus,
            isValid,
            isSubmitting: statusIsSubmitting 
          }) => (
            <>
              <div
                className={`mb-6 overflow-hidden ${
                  tailwindClasses.cardWithShadow || "rounded-lg "
                }`}
              >
                <ArticleTable
                  currentItems={currentItems}
                  getArticleTitle={getArticleTitle}
                  getArticleAuthor={getArticleAuthor}
                  getArticleDate={getArticleDate}
                  handleActionPrimary={
                    userRole === "Administrateur" ? undefined : (article) => {
                      console.log("🚀 Publication article initiée:", {
                        articleId: article.id_art,
                        title: getArticleTitle(article),
                        user: "HiQaody",
                        timestamp: "2025-08-31 07:45:45",
                      });
                      handlePublishArticle(article);
                    }
                  }
                  handleViewDetails={
                    userRole === "Administrateur" ? undefined : navigateToDetails
                  }
                  showEditButton={userRole !== "Administrateur"}
                  showPrimaryButton={userRole !== "Administrateur"}
                  actionPrimaryText={userRole === "Administrateur" ? null : "Publier"}
                  actionPrimaryIcon={MdPublishedWithChanges}
                  handleEditArticle={
                    userRole === "Administrateur" ? undefined : navigateToEdit
                  }
                  showContentButton={userRole !== "Administrateur"}
                  handleContentArticle={
                    userRole === "Administrateur" ? undefined : handleContentArticle
                  }
                  showStatusButton={userRole !== "Administrateur"}
                  handleStatusArticle={
                    userRole === "Administrateur" ? undefined : (article) => {
                      console.log("📊 Attribution statut article publiable:", {
                        articleId: article.id_art,
                        user: "HiQaody",
                        timestamp: "2025-08-31 07:45:45",
                      });
                      initializeStatusAttribution(article);
                      setModalTitle("Attribuer des status");
                      setModalSize("2xl");
                      setIsModalOpen(true);
                    }
                  }
                  primaryColor={colors.primary}
                  dateColumnLabel="date de validation"
                />

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  handlePageChange={handlePageChange}
                  itemsPerPage={itemsPerPage}
                  handleItemsPerPageChange={handleItemsPerPageChange}
                  indexOfFirstItem={indexOfFirstItem}
                  indexOfLastItem={indexOfLastItem}
                  totalItems={totalItems}
                  primaryColor={colors.primary}
                />
              </div>

              {/* Modal avec StatusHandler */}
              <Modal
                isOpen={isModalOpen}
                onClose={() => {
                  console.log("🔄 Fermeture modal articles publiables:", {
                    modalTitle,
                    user: "HiQaody",
                    timestamp: "2025-08-31 07:45:45",
                  });
                  setIsModalOpen(false);
                }}
                title={modalTitle}
                width={modalSize}
              >
                {modalTitle === "Attribuer des status" && statusSelectedArticle ? (
                  <div className="bg-white">
                    {/* En-tête de la modal avec informations de l'article */}
                    <div className="border-b border-gray-200 px-6 py-4">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                            <svg
                              className="h-5 w-5 text-green-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="truncate text-lg font-semibold text-gray-900">
                            {getArticleTitle(statusSelectedArticle)}
                          </h3>
                          <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                            <span>Par {getArticleAuthor(statusSelectedArticle)}</span>
                            <span>•</span>
                            <span>
                              Validé le {getArticleDate(statusSelectedArticle)}
                            </span>
                            <span>•</span>
                            <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                              Article publiable
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Information contextuelle */}
                    <div className="border-b border-gray-100 bg-green-50 px-6 py-3">
                      <div className="flex items-center space-x-2 text-sm text-green-800">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-medium">Article validé et prêt pour publication</span>
                      </div>
                    </div>

                    {/* Corps de la modal */}
                    <div className="space-y-6 px-6 py-6">
                      {/* Information contextuelle pour articles publiables */}
                      <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <svg
                              className="mt-0.5 h-5 w-5 text-green-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <h5 className="text-sm font-medium text-green-900">
                              Changement de statut d'article publiable
                            </h5>
                            <p className="mt-1 text-sm text-green-700">
                              Cet article a été validé comme publiable. Vous pouvez le publier 
                              définitivement, le rétrograder pour révision, ou confirmer son 
                              statut avec un nouveau commentaire.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Sélection du nouveau statut */}
                      {StatusSelector}

                      {/* Section commentaire */}
                      {CommentInput}

                      {/* Messages d'erreur et de succès */}
                      {StatusMessages}
                    </div>

                    {/* Footer avec boutons */}
                    <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
                      <button
                        type="button"
                        className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                        onClick={() => {
                          console.log("❌ Annulation changement statut article publiable:", {
                            user: "HiQaody",
                            timestamp: "2025-08-31 07:45:45",
                          });
                          setIsModalOpen(false);
                        }}
                        disabled={statusIsSubmitting}
                      >
                        Annuler
                      </button>
                      
                      <button
                        className={`rounded-lg px-6 py-2 text-sm font-medium text-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                          isValid && !statusIsSubmitting
                            ? "bg-[#57A863] hover:bg-[#4a9154] focus:ring-[#57A863] shadow-lg hover:shadow-xl"
                            : "cursor-not-allowed bg-gray-400 focus:ring-gray-400"
                        }`}
                        onClick={() => {
                          console.log("🚀 Soumission changement statut article publiable:", {
                            articleId: statusSelectedArticle.id_art,
                            newStatus: statusSelectedArticle.id_stat,
                            isValid,
                            user: "HiQaody",
                            timestamp: "2025-08-31 07:45:45",
                          });
                          handleSubmitStatus();
                        }}
                        disabled={!isValid || statusIsSubmitting}
                      >
                        {statusIsSubmitting ? (
                          <span className="flex items-center">
                            <svg
                              className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Attribution en cours...
                          </span>
                        ) : (
                          <span className="flex items-center">
                            <MdAssignment className="mr-2 h-4 w-4" />
                            Attribuer le statut
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                ) : modalTitle === "Publier cet article" ? (
                  publicationModalContent
                ) : (
                  modalContent
                )}

                {/* Footer pour modal de publication */}
                {modalTitle === "Publier cet article" && (
                  <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
                    <button
                      type="button"
                      className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                      onClick={() => {
                        console.log("❌ Annulation publication article:", {
                          articleId: selectedArticle?.id_art,
                          user: "HiQaody",
                          timestamp: "2025-08-31 07:45:45",
                        });
                        setIsModalOpen(false);
                      }}
                      disabled={isSubmitting}
                    >
                      Annuler
                    </button>
                    
                    <button
                      type="button"
                      className={`rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm ${
                        isSubmitting || !coverImage
                          ? "cursor-not-allowed bg-opacity-60"
                          : "hover:opacity-90 focus:ring-offset"
                      }`}
                      style={{
                        backgroundColor:
                          isSubmitting || !coverImage
                            ? `${colors.primary}80`
                            : colors.primary,
                      }}
                      onClick={handleSubmitPublication}
                      disabled={isSubmitting || !coverImage}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center">
                          <svg
                            className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Publication en cours...
                        </span>
                      ) : (
                        <span className="flex items-center">
                          <MdPublishedWithChanges className="mr-2 h-4 w-4" />
                          Publier l'article
                        </span>
                      )}
                    </button>
                  </div>
                )}
              </Modal>
            </>
          )}
        </StatusHandler>
      </div>
    </ConfigProvider>
  );
};

export default ArticlePubliable;